import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem('auth_token');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    // Handle OAuth callback - exchange session_id for token
    if (window.location.hash?.includes('session_id=')) {
      const sessionIdMatch = window.location.hash.match(/session_id=([^&]+)/);
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        // Clear hash immediately to prevent re-processing
        window.history.replaceState(null, '', window.location.pathname);
        
        // Exchange session
        exchangeSessionInternal(sessionId);
        return;
      }
    }
    checkAuth();
  }, []);

  const exchangeSessionInternal = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, {
        withCredentials: true
      });
      console.log('Session exchange response:', response.data);
      
      // Save the token
      if (response.data.token) {
        setToken(response.data.token);
        localStorage.setItem('auth_token', response.data.token);
      }
      
      const userData = response.data.user || response.data;
      setUser(userData);
    } catch (error) {
      console.error('Session exchange failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('auth_token', newToken);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    setToken(newToken);
    setUser(userData);
    localStorage.setItem('auth_token', newToken);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/archive';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    return exchangeSessionInternal(sessionId);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Continue with logout even if API fails
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    loginWithGoogle,
    exchangeSession,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isSubscribed: user?.subscription_status === 'active'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
